// Asientos Recurrentes y plantillas de póliza. Contabilidad → Movimientos.
//
// Resuelve el trabajo que el contador venía retecleando cada mes: las mismas
// provisiones, depreciación, amortización de seguros y devengos. Una plantilla
// con periodicidad es un asiento recurrente; una con periodicidad «manual» es
// una póliza modelo que se usa cuantas veces haga falta.
//
// Lo importante de la pantalla no es generar: es que NO se pueda generar dos
// veces el mismo período. Una provisión duplicada no se nota hasta el cierre.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, Plus, Trash2, Repeat, Play, Pencil, AlertTriangle, CheckCircle2, History,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const CONT = { headers: { 'Content-Profile': 'contabilidad', 'Accept-Profile': 'contabilidad' } };
const field = 'flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const usd = (v: number) => (Number(v) || 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const num = (v: number) => (Number(v) || 0).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PERIODICIDADES = [
  { v: 'manual', t: 'Manual (póliza modelo)' },
  { v: 'mensual', t: 'Mensual' },
  { v: 'bimensual', t: 'Bimensual' },
  { v: 'trimestral', t: 'Trimestral' },
  { v: 'semestral', t: 'Semestral' },
  { v: 'anual', t: 'Anual' },
];
const etiquetaPer = (v: string) => PERIODICIDADES.find((p) => p.v === v)?.t ?? v;

interface CuentaOpt { id: string; codigo: string; nombre: string; es_movimiento?: boolean }
interface Fila {
  id: string; nombre: string; glosa: string | null; periodicidad: string; dia_mes: number;
  vigente_desde: string | null; vigente_hasta: string | null; activo: boolean;
  lineas: number; total: number; generaciones: number; ultima_generacion: string | null;
  periodo_actual: string | null; periodo_generado: boolean; pendiente: boolean;
}
interface LineaEd {
  key: number; cuenta_id: string; glosa: string; debe: number; haber: number;
  centro_costo_id: string; proyecto_id: string;
}

let _k = 1;
const nuevaLinea = (): LineaEd => ({ key: _k++, cuenta_id: '', glosa: '', debe: 0, haber: 0, centro_costo_id: '', proyecto_id: '' });

export function AsientosRecurrentes() {
  const qc = useQueryClient();
  const [editando, setEditando] = useState<string | null | undefined>(undefined); // undefined = lista
  const { data: filas = [], isLoading } = useQuery<Fila[]>({
    queryKey: ['cont-plantillas'],
    queryFn: async () => (await apiClient.post('/rpc/cont_plantillas', {}, CONT)).data || [],
  });

  const pendientes = filas.filter((f) => f.pendiente);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><Repeat className="w-5 h-5" /> Asientos recurrentes</h1>
          <p className="text-sm text-muted-foreground">
            Las provisiones, depreciaciones y devengos que se repiten cada período, definidos una vez.
          </p>
        </div>
        {editando === undefined && (
          <Button onClick={() => setEditando(null)}><Plus className="w-4 h-4 mr-1" /> Nueva plantilla</Button>
        )}
      </div>

      {editando !== undefined
        ? <Editor id={editando} onCerrar={() => { setEditando(undefined); void qc.invalidateQueries({ queryKey: ['cont-plantillas'] }); }} />
        : (
          <>
            {pendientes.length > 0 && (
              <p className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                {pendientes.length === 1
                  ? `Al período en curso le falta generar «${pendientes[0].nombre}».`
                  : `Al período en curso le faltan ${pendientes.length} asientos recurrentes por generar.`}
              </p>
            )}
            {isLoading
              ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>
              : <Lista filas={filas} onEditar={(id) => setEditando(id)} />}
          </>
        )}
    </div>
  );
}

function Lista({ filas, onEditar }: { filas: Fila[]; onEditar: (id: string) => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['cont-plantillas'] });
    void qc.invalidateQueries({ queryKey: ['cont-asientos'] });
  };
  const generar = useMutation({
    mutationFn: async (b: { _id: string; _fecha?: string; _factor?: number }) =>
      (await apiClient.post('/rpc/cont_plantilla_generar', b, CONT)).data,
    onSuccess: (r: { message?: string }) => { toast.success(r?.message || 'Asiento generado'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/cont_plantilla_eliminar', { _id: id }, CONT)).data,
    onSuccess: () => { toast.success('Plantilla eliminada'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (filas.length === 0) {
    return (
      <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">
        Todavía no hay plantillas. Crea una con los asientos que repites cada mes —provisiones,
        depreciación, amortización de seguros— y deja de reteclearlos.
      </CardContent></Card>
    );
  }

  return (
    <>
      {dlg.ui}
      <Card>
      <CardHeader className="pb-3"><CardTitle className="text-base">Plantillas ({filas.length})</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b">
            <tr>
              <th className="py-2 pr-3 text-left w-10">N°</th>
              <th className="py-2 pr-3 text-left">Nombre</th>
              <th className="py-2 pr-3 text-left">Periodicidad</th>
              <th className="py-2 pr-3 text-right">Importe</th>
              <th className="py-2 pr-3 text-left">Período en curso</th>
              <th className="py-2 pr-3 text-right">Generados</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => (
              <tr key={f.id} className={`border-b last:border-0 hover:bg-muted/40 ${f.activo ? '' : 'opacity-60'}`}>
                <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                <td className="py-2 pr-3">
                  {f.nombre}
                  {!f.activo && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">inactiva</span>}
                  <span className="block text-xs text-muted-foreground">{f.glosa || `${f.lineas} líneas`}</span>
                </td>
                <td className="py-2 pr-3">
                  {etiquetaPer(f.periodicidad)}
                  {f.periodicidad !== 'manual' && <span className="block text-xs text-muted-foreground">día {f.dia_mes}</span>}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">{num(Number(f.total))}</td>
                <td className="py-2 pr-3">
                  {f.periodicidad === 'manual'
                    ? <span className="text-xs text-muted-foreground">— se usa a demanda —</span>
                    : f.periodo_generado
                    ? <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-300"><CheckCircle2 className="w-3.5 h-3.5" /> ya generado</span>
                    : f.pendiente
                    ? <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> pendiente</span>
                    : <span className="text-xs text-muted-foreground">fuera de vigencia</span>}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {f.generaciones}
                  {f.ultima_generacion && <span className="block text-xs text-muted-foreground">{String(f.ultima_generacion).slice(0, 10)}</span>}
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle"
                    title="Editar" onClick={() => onEditar(f.id)}><Pencil className="w-4 h-4" /></button>
                  {f.activo && (
                    <Button size="sm" variant={f.pendiente ? 'default' : 'outline'} isLoading={generar.isPending}
                      onClick={async () => {
                        const msg = f.periodicidad === 'manual'
                          ? `¿Generar un asiento desde «${f.nombre}»?`
                          : `¿Generar «${f.nombre}» para el período en curso?`;
                        const detalle = f.periodicidad === 'manual' ? undefined : 'Si ya se generó, el sistema lo rechazará indicando con qué asiento.';
                        if (await dlg.confirmar(msg, { detalle })) generar.mutate({ _id: f.id });
                      }}>
                      <Play className="w-3.5 h-3.5 mr-1" /> Generar
                    </Button>
                  )}
                  {f.generaciones === 0 && (
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1 align-middle"
                      title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar «${f.nombre}»?`, { peligro: true })) eliminar.mutate(f.id); }}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      </Card>
    </>
  );
}

function Editor({ id, onCerrar }: { id: string | null; onCerrar: () => void }) {
  const { data: cuentas = [] } = useQuery<CuentaOpt[]>({
    queryKey: ['cont-plan'], queryFn: async () => (await apiClient.post('/rpc/cont_plan', {}, CONT)).data || [],
  });
  const movimiento = useMemo(
    () => cuentas.filter((c) => c.es_movimiento !== false), [cuentas]);

  const [cab, setCab] = useState({
    nombre: '', glosa: '', periodicidad: 'mensual', dia_mes: 1,
    vigente_desde: '', vigente_hasta: '', activo: true, notas: '',
  });
  const [lineas, setLineas] = useState<LineaEd[]>([nuevaLinea(), nuevaLinea()]);

  const { data: det } = useQuery<Record<string, unknown> | null>({
    queryKey: ['cont-plantilla', id], enabled: !!id,
    queryFn: async () => (await apiClient.post('/rpc/cont_plantilla_obtener', { _id: id }, CONT)).data,
  });
  useEffect(() => {
    if (!det) return;
    const d = det as {
      nombre?: string; glosa?: string; periodicidad?: string; dia_mes?: number;
      vigente_desde?: string; vigente_hasta?: string; activo?: boolean; notas?: string;
      lineas?: { cuenta_id?: string; glosa?: string; debe?: number; haber?: number; centro_costo_id?: string; proyecto_id?: string }[];
      historial?: { periodo: string; fecha: string; numero: number | null }[];
    };
    setCab({
      nombre: d.nombre || '', glosa: d.glosa || '', periodicidad: d.periodicidad || 'mensual',
      dia_mes: Number(d.dia_mes) || 1,
      vigente_desde: d.vigente_desde ? String(d.vigente_desde).slice(0, 10) : '',
      vigente_hasta: d.vigente_hasta ? String(d.vigente_hasta).slice(0, 10) : '',
      activo: d.activo !== false, notas: d.notas || '',
    });
    setLineas((d.lineas || []).map((l) => ({
      key: _k++, cuenta_id: l.cuenta_id || '', glosa: l.glosa || '',
      debe: Number(l.debe) || 0, haber: Number(l.haber) || 0,
      centro_costo_id: l.centro_costo_id || '', proyecto_id: l.proyecto_id || '',
    })));
  }, [det]);

  const historial = (det as { historial?: { periodo: string; fecha: string; numero: number | null }[] } | undefined)?.historial ?? [];

  const setL = (key: number, patch: Partial<LineaEd>) =>
    setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const totDebe = lineas.reduce((s, l) => s + (Number(l.debe) || 0), 0);
  const totHaber = lineas.reduce((s, l) => s + (Number(l.haber) || 0), 0);
  const cuadra = Math.abs(totDebe - totHaber) < 0.005;

  const guardar = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/cont_plantilla_guardar', {
      _id: id, _cab: { ...cab, vigente_desde: cab.vigente_desde || null, vigente_hasta: cab.vigente_hasta || null },
      _lineas: lineas.map((l) => ({
        cuenta_id: l.cuenta_id, glosa: l.glosa.trim() || null,
        debe: Number(l.debe) || 0, haber: Number(l.haber) || 0,
        centro_costo_id: l.centro_costo_id || null, proyecto_id: l.proyecto_id || null,
      })),
    }, CONT)).data,
    onSuccess: () => { toast.success('Plantilla guardada'); onCerrar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puede = cab.nombre.trim() && lineas.length >= 2
    && lineas.every((l) => l.cuenta_id) && cuadra && totDebe > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">{id ? 'Editar plantilla' : 'Nueva plantilla'}</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Nombre *</label>
            <input className={field} value={cab.nombre} onChange={(e) => setCab({ ...cab, nombre: e.target.value })}
              placeholder="Depreciación de activos fijos" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Glosa del asiento</label>
            <input className={field} value={cab.glosa} onChange={(e) => setCab({ ...cab, glosa: e.target.value })}
              placeholder="Si la dejas vacía se usa el nombre" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Periodicidad</label>
            <select className={field} value={cab.periodicidad} onChange={(e) => setCab({ ...cab, periodicidad: e.target.value })}>
              {PERIODICIDADES.map((p) => <option key={p.v} value={p.v}>{p.t}</option>)}
            </select>
          </div>
          {cab.periodicidad !== 'manual' && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">Día del período</label>
              <input type="number" min="1" max="28" className={field} value={cab.dia_mes}
                onChange={(e) => setCab({ ...cab, dia_mes: Number(e.target.value) })} />
              <p className="mt-1 text-xs text-muted-foreground">Máximo 28: así la fecha existe en todos los meses.</p>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Rige desde</label>
            <input type="date" className={field} value={cab.vigente_desde} onChange={(e) => setCab({ ...cab, vigente_desde: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Hasta</label>
            <input type="date" className={field} value={cab.vigente_hasta} onChange={(e) => setCab({ ...cab, vigente_hasta: e.target.value })} />
          </div>
          <div className="md:col-span-3">
            <label className="text-xs font-medium text-muted-foreground">Notas</label>
            <input className={field} value={cab.notas} onChange={(e) => setCab({ ...cab, notas: e.target.value })} />
          </div>
          <label className="flex items-end gap-2 pb-1 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={cab.activo}
              onChange={(e) => setCab({ ...cab, activo: e.target.checked })} />
            Activa
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Líneas del asiento</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setLineas((ls) => [...ls, nuevaLinea()])}>
            <Plus className="w-4 h-4 mr-1" /> Línea
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-3 text-left w-10">N°</th>
                <th className="py-2 pr-3 text-left">Cuenta</th>
                <th className="py-2 pr-3 text-left">Glosa de la línea</th>
                <th className="py-2 pr-3 text-right w-32">Debe</th>
                <th className="py-2 pr-3 text-right w-32">Haber</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lineas.map((l, i) => (
                <tr key={l.key} className="border-b last:border-0">
                  <td className="py-1 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1 pr-3">
                    <select className={field} value={l.cuenta_id} onChange={(e) => setL(l.key, { cuenta_id: e.target.value })}>
                      <option value="">— Selecciona —</option>
                      {movimiento.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-3"><input className={field} value={l.glosa} onChange={(e) => setL(l.key, { glosa: e.target.value })} /></td>
                  <td className="py-1 pr-3">
                    <input type="number" step="0.01" min="0" className={`${field} text-right`} value={l.debe}
                      onChange={(e) => setL(l.key, { debe: Number(e.target.value), haber: Number(e.target.value) > 0 ? 0 : l.haber })} />
                  </td>
                  <td className="py-1 pr-3">
                    <input type="number" step="0.01" min="0" className={`${field} text-right`} value={l.haber}
                      onChange={(e) => setL(l.key, { haber: Number(e.target.value), debe: Number(e.target.value) > 0 ? 0 : l.debe })} />
                  </td>
                  <td className="py-1">
                    <button className="text-muted-foreground hover:text-destructive disabled:opacity-30" disabled={lineas.length <= 2}
                      onClick={() => setLineas((ls) => ls.filter((x) => x.key !== l.key))}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t font-medium">
              <tr className={cuadra ? '' : 'text-red-600'}>
                <td colSpan={3} className="py-2 pr-3 text-right text-muted-foreground">
                  {cuadra ? 'Cuadra' : `Descuadre de ${usd(Math.abs(totDebe - totHaber))}`}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">{num(totDebe)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{num(totHaber)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
          <p className="mt-3 text-xs text-muted-foreground">
            Los importes son los de referencia. Al generar puedes aplicar un factor si ese período cambia.
          </p>
        </CardContent>
      </Card>

      {historial.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Ya generado ({historial.length})</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr><th className="py-2 pr-3 text-left">Período</th><th className="py-2 pr-3 text-left">Fecha</th><th className="py-2 pr-3 text-left">Asiento</th></tr>
              </thead>
              <tbody>
                {historial.map((h, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 pr-3">{String(h.periodo).slice(0, 7).split('-').reverse().join('/')}</td>
                    <td className="py-2 pr-3">{String(h.fecha).slice(0, 10)}</td>
                    <td className="py-2 pr-3 font-mono text-xs">n.º {h.numero ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCerrar}>Cancelar</Button>
        <Button isLoading={guardar.isPending} disabled={!puede} onClick={() => guardar.mutate()}>
          {id ? 'Guardar cambios' : 'Crear plantilla'}
        </Button>
      </div>
    </div>
  );
}

export default AsientosRecurrentes;
